import React from 'react';
import { useNavigate } from 'react-router-dom';
import AppActionMenu from '../../../../components/AppActionMenu';
import AppQuickAction from '../../../../components/AppQuickAction';

export default function LoansTable({
  records = [],
  isLoading = false,
  onApprove,
  onReject,
  onDelete,
  onResetFilters,
}) {
  const navigate = useNavigate();

  const formatCurrency = (val) => {
    if (typeof val !== 'number' && isNaN(Number(val))) return '₹0.00';
    return Number(val).toLocaleString('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const getStatusBadge = (status) => {
    if (status === 'Approved') {
      return (
        <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-500/10 px-2.5 py-0.5 text-[11px] font-bold text-emerald-600 dark:text-emerald-400">
          <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
          Approved
        </span>
      );
    }
    if (status === 'Rejected') {
      return (
        <span className="inline-flex items-center gap-1.5 rounded-full bg-rose-500/10 px-2.5 py-0.5 text-[11px] font-bold text-rose-600 dark:text-rose-400">
          <span className="h-1.5 w-1.5 rounded-full bg-rose-500" />
          Rejected
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-500/10 px-2.5 py-0.5 text-[11px] font-bold text-amber-600 dark:text-amber-400">
        <span className="h-1.5 w-1.5 rounded-full bg-amber-500" />
        Pending
      </span>
    );
  };

  if (isLoading) {
    return (
      <div className="overflow-hidden rounded-2xl border border-outline-variant/25 bg-surface-container-lowest p-16 text-center shadow-sm">
        <div className="flex flex-col items-center justify-center gap-3">
          <div className="h-8 w-8 animate-spin rounded-full border-3 border-primary border-t-transparent" />
          <p className="text-xs font-semibold text-on-surface-variant">Loading Loan Applications...</p>
        </div>
      </div>
    );
  }

  if (records.length === 0) {
    return (
      <div className="overflow-hidden rounded-2xl border border-outline-variant/25 bg-surface-container-lowest p-16 text-center shadow-sm">
        <div className="flex flex-col items-center justify-center gap-3">
          <div className="flex h-16 w-16 items-center justify-center rounded-full bg-primary/10 text-primary">
            <span className="material-symbols-outlined text-3xl">account_balance</span>
          </div>
          <p className="text-sm font-bold text-on-surface">No loan records found</p>
          <p className="text-xs text-on-surface-variant max-w-sm">
            No loan applications match your search criteria. Submit a new loan request using the button above.
          </p>
          {onResetFilters && (
            <button
              type="button"
              onClick={onResetFilters}
              className="mt-2 rounded-xl bg-primary px-5 py-2 text-xs font-bold text-on-primary shadow-sm hover:bg-primary/90 transition cursor-pointer"
            >
              Reset Filters
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="overflow-hidden rounded-2xl border border-outline-variant/25 bg-surface-container-lowest shadow-sm">
      <div className="overflow-x-auto">
        <table className="w-full min-w-[950px] text-left text-sm">
          <thead>
            <tr className="border-b border-outline-variant/20 bg-surface-container-low/60 text-xs font-bold uppercase tracking-[0.08em] text-on-surface-variant">
              <th className="px-5 py-3.5">Employee Name</th>
              <th className="px-4 py-3.5">Loan Date</th>
              <th className="px-4 py-3.5 text-right">Loan Amount</th>
              <th className="px-4 py-3.5 text-right">EMI Amount</th>
              <th className="px-4 py-3.5">Reason</th>
              <th className="px-4 py-3.5 text-center">Status</th>
              <th className="px-4 py-3.5 text-center">Quick Action</th>
              <th className="w-16 px-5 py-3.5 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-outline-variant/15">
            {records.map((row) => (
              <tr
                key={row.id}
                onClick={() => navigate(`/payroll/loans/entry?id=${row.id}`)}
                className="transition-colors hover:bg-surface-container-low/50 cursor-pointer group"
              >
                {/* Employee Name & ID */}
                <td className="px-5 py-3.5 font-bold text-on-surface">
                  <div className="flex flex-col">
                    <span className="font-bold text-on-surface group-hover:text-primary transition-colors">
                      {row.name}
                    </span>
                    {row.employeeId && (
                      <span className="text-[11px] font-normal text-on-surface-variant">
                        {row.employeeId}
                      </span>
                    )}
                  </div>
                </td>

                {/* Loan Date */}
                <td className="px-4 py-3.5 text-xs font-medium text-on-surface whitespace-nowrap">
                  {row.loanDateDisplay || row.loanDate || row.applyDate}
                </td>

                {/* Loan Amount */}
                <td className="px-4 py-3.5 text-right font-bold text-on-surface font-mono whitespace-nowrap">
                  {formatCurrency(row.loanAmount)}
                </td>

                {/* EMI Amount */}
                <td className="px-4 py-3.5 text-right font-semibold text-on-surface-variant font-mono whitespace-nowrap">
                  {formatCurrency(row.emiAmount)}
                </td>

                {/* Reason */}
                <td className="px-4 py-3.5 text-xs text-on-surface-variant">
                  <span className="inline-flex items-center rounded-md bg-surface-container-high px-2 py-0.5 text-[11px] font-semibold text-on-surface">
                    {row.reason || '—'}
                  </span>
                </td>

                {/* Status Badge */}
                <td className="px-4 py-3.5 text-center">
                  {getStatusBadge(row.status)}
                </td>

                {/* Quick Action (Approve / Reject) */}
                <td
                  className="px-4 py-3.5 text-center"
                  onClick={(e) => e.stopPropagation()}
                >
                  <AppQuickAction
                    status={row.status}
                    onApprove={() => onApprove?.(row.id, row.name)}
                    onReject={() => onReject?.(row.id, row.name)}
                    approveTitle="Quick Approve Loan"
                    rejectTitle="Quick Reject Loan"
                  />
                </td>

                {/* Actions Menu */}
                <td
                  className="w-16 px-5 py-3.5 text-right"
                  onClick={(e) => e.stopPropagation()}
                >
                  <AppActionMenu
                    triggerLabel=""
                    triggerIcon="more_vert"
                    showChevron={false}
                    triggerClassName="rounded-lg p-1.5 text-outline-variant transition-colors hover:bg-surface-container-low hover:text-primary bg-transparent cursor-pointer"
                    items={[
                      { key: 'edit', label: 'View / Edit', icon: 'edit' },
                      { key: 'delete', label: 'Delete', icon: 'delete', danger: true },
                    ]}
                    onSelect={(key) => {
                      if (key === 'edit') {
                        navigate(`/payroll/loans/entry?id=${row.id}`);
                      } else if (key === 'delete') {
                        onDelete?.(row.id, row.name);
                      }
                    }}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
